import { FC, useState } from 'react';
import {
	Button,
	ButtonAppearance,
	ButtonColor,
	DataDownload,
	Dropdown,
	FileCopyIcon,
	Label,
	LoaderBars,
	Menu,
	Trash,
	useOpen,
	MenuDotsIcon,
	NetworkConnectionIcon,
} from '@aleph/shared-components';
import dayjs from 'dayjs';
import useCreateSpotifyReport from 'wb-spotify/app/routes/reports/create/useCreateSpotifyReport';
import { useMutation } from '@apollo/client';
import {
	downloadSpotifyReport, getDatesFromSchedule, getReportClientIds, ReportRow,
} from 'wb-spotify/app/routes/reports/utils';
import useReportActionsOpen from 'wb-spotify/app/routes/reports/hooks/useReportActionsOpen';
import { HandleUpdateRow } from 'wb-spotify/app/routes/hooks/useUpdateRows';
import { useTranslation } from 'react-i18next';
import { DeleteSpotifyReportDocument, SpotifyReportsDocument } from 'wb-spotify/graphql/generated';
import { LIFE_TIME_METRICS } from 'wb-spotify/app/routes/superGrid/statistics/utils';
import useSupergridSettings from 'wb-spotify/app/routes/superGrid/hooks/useSupergridSettings';
import { SpotifyReportSchedule, TIME_REGEX } from 'wb-spotify/app/common/utils';
import SpotifyReportSharingModal from 'wb-spotify/app/routes/reports/components/sharing';

import useSpotifyRolePermissions from 'wb-spotify/app/common/hooks/useSpotifyPermissions';

interface IProps {
	report: ReportRow;
	handleUpdateRow: HandleUpdateRow<ReportRow>;
}

const SpotifyReportsActions: FC<IProps> = ({ report, handleUpdateRow }) => {
	const { t } = useTranslation();
	const { toggleLifetimeStats } = useSupergridSettings();

	const {
		setFilters, setName, setMetrics, setBreakdowns, setDates,
		setSchedule, setUserGroupIds, setTime, setPrivateUserGroupIds,
	} = useCreateSpotifyReport();

	const { setReportActionsOpenId } = useReportActionsOpen();
	const [downloading, setDownloading] = useState<boolean>(false);
	const { canShareReport, canEditReport } = useSpotifyRolePermissions();

	const [sharingOpen, { onOpen: onOpenSharing, onClose: onCloseSharing }] = useOpen(false);

	const [deleteSpotifyReport, { loading: deleting }] = useMutation(DeleteSpotifyReportDocument, {
		refetchQueries:      [SpotifyReportsDocument],
		awaitRefetchQueries: true,
	});

	const onDuplicateReport = () => {
		const clientIds = getReportClientIds(report?.reportFiltersOptions);
		setReportActionsOpenId(undefined);

		// @ts-ignore TODO might not work if metric is not in the list
		const isLifetimeStats = LIFE_TIME_METRICS.some(metric => report.reportMetrics?.includes?.(metric))
		// @ts-ignore
		|| report.reportDimensions?.every(breakdown => breakdown !== 'date');

		if (isLifetimeStats) {
			toggleLifetimeStats();
		}

		setFilters({
			userGroupId: [{ value: clientIds }],
		});

		setName(`Duplicate of ${report.reportName}`);
		// @ts-ignore
		setBreakdowns((report.reportDimensions));
		// @ts-ignore
		setMetrics(report.reportMetrics);
		setUserGroupIds(clientIds ?? []);
		setPrivateUserGroupIds(report.userGroupId ?? []);

		const dateRangeValues = report.reportDateRangeOptions?.dateRangeValue ?? [];

		if (dateRangeValues?.length > 0) {
			setDates({ from: dayjs(dateRangeValues?.[0]), to: dayjs(dateRangeValues?.[1]) });
		} else {
			const datesFromSchedule = getDatesFromSchedule(
				{ selectedSchedule: report.reportSchedule as SpotifyReportSchedule },
			);

			setDates(datesFromSchedule);
		}

		// Keep typecast since in schema it is string, but in reality it is enum
		setSchedule(report.reportSchedule as SpotifyReportSchedule);

		if (TIME_REGEX.test(report.reportSchedule)) {
			setSchedule(SpotifyReportSchedule.OnlyOnce);
			setTime(report.reportSchedule);
		}
	};

	return (
		<div
			id={`report_${report.reportId}`}
			className="flex justify-center items-center gap-[12px] relative"
		>
			{sharingOpen && (
				<SpotifyReportSharingModal
					report={report}
					onClose={onCloseSharing}
					handleUpdateRow={handleUpdateRow}
				/>
			)}
			{canShareReport
				&& <Button
					leftNode={<NetworkConnectionIcon strokeWidth={1.5} />}
					appearance={ButtonAppearance.GHOST}
					onClick={onOpenSharing}
					data-testid="share-report-button"
				/>
			}
			{(downloading
				? (
					<LoaderBars
						className="animate-spin"
						width={16}
						height={16}
					/>
				)
				: (
					<Button
						leftNode={<DataDownload />}
						appearance={ButtonAppearance.GHOST}
						onClick={async () => {
							setDownloading(true);
							await downloadSpotifyReport(report);
							setDownloading(false);
						}}
						data-testid="download-report-button"
					/>
				)
			)}

			{canEditReport
				&& <Dropdown
					onOpen={() => {
						setReportActionsOpenId(report.reportId ?? '');
					}}
					arrow={false}
					onClose={() => {
						setReportActionsOpenId(undefined);
					}}
					color={ButtonColor.WISE_BLUE}
					data-testid="dropdown-report-button"
					triggerControl={({ isOpen, closeDropdown, openDropdown }) => (
						<Button
							leftNode={<MenuDotsIcon />}
							appearance={ButtonAppearance.GHOST}
							onClick={isOpen ? closeDropdown : openDropdown}
							data-testid="share-report-button"
						/>
					)}
				>
					<Menu.Item onClick={onDuplicateReport}>
						<FileCopyIcon
							width={12}
							height={12}
						/>
						<Label>
							Duplicate
						</Label>
					</Menu.Item>
					<Menu.Item onClick={async () => {
						await deleteSpotifyReport({
							variables: { reportId: report?.reportId ?? '' },
						});
						setReportActionsOpenId(undefined);
						handleUpdateRow({
							row:      report,
							isDelete: true,
						});
					}}
					>
						{
							deleting ? <LoaderBars
								className="animate-spin"
								width={12}
								height={12}
							/> : <Trash
								width={12}
								height={12}
							/>
						}
						<Label>{t('sc: delete', 'Delete')}</Label>
					</Menu.Item>
				</Dropdown>
			}
		</div>
	);
};

export default SpotifyReportsActions;
